import React, { useState } from "react";
import AuthLayout from "./AuthLayout";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { VisibilityOff, Visibility, LockOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useConfirmPasswordMutation } from "../../services/auth";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmPasswordApi, { isLoading }] = useConfirmPasswordMutation();

  const [passwordVisibility, setPasswordVisibility] = useState({
    new: false,
    confirm: false,
  });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});

  const handlePasswordChange = (field: "new" | "confirm", value: string) => {
    const updatedNew = field === "new" ? value : newPassword;
    const updatedConfirm = field === "confirm" ? value : confirmPassword;

    if (field === "new") setNewPassword(value);
    if (field === "confirm") setConfirmPassword(value);

    // Dynamic validation if there are existing errors
    if (errors.newPassword || errors.confirmPassword) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        if (updatedNew) delete nextErrors.newPassword;
        if (updatedConfirm) delete nextErrors.confirmPassword;

        if (updatedNew && updatedConfirm && updatedNew !== updatedConfirm) {
          nextErrors.confirmPassword = "Passwords do not match";
        } else if (updatedNew && updatedConfirm && updatedNew === updatedConfirm) {
          delete nextErrors.confirmPassword;
        }

        return nextErrors;
      });
    }
  };

  const toggleVisibility = (field: "new" | "confirm") => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validateResetForm = (newPass: string, confirmPass: string) => {
    let hasError = false;
    const newErrors: any = {};
    if (!newPass) {
      newErrors.newPassword = "New password is required";
      hasError = true;
    }
    if (!confirmPass) {
      newErrors.confirmPassword = "Confirm password is required";
      hasError = true;
    }
    if (newPass && confirmPass && newPass !== confirmPass) {
      newErrors.confirmPassword = "Passwords do not match";
      hasError = true;
    }
    return { hasError, newErrors };
  };

  const parseResetError = (err: any) => {
    if (err?.data?.message) return err.data.message;
    if (err?.data && typeof err.data === 'string') return err.data;
    if (err?.error) return typeof err.error === 'string' ? err.error : JSON.stringify(err.error) || String(err.error);
    if (err?.message) return typeof err.message === 'string' ? err.message : String(err.message);
    if (err?.data) return JSON.stringify(err.data);
    return "Failed to reset password";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const { hasError, newErrors } = validateResetForm(newPassword, confirmPassword);
    if (hasError) {
      setErrors(newErrors);
      return;
    }

    try {
      const { email } = location.state || {};

      // Construct standard reset payload according to Swagger
      const payload: any = {
        email: email || "", // ensure email is provided or gracefully empty
        password: newPassword,
      };

      await confirmPasswordApi(payload).unwrap();
      toast.success("Password reset successfully! Please log in.");
      navigate("/");
    } catch (err: any) {
      console.error("Confirm Password API Error:", err);
      toast.error(String(parseResetError(err)));
    }
  };

  return (
    <AuthLayout
      subtitle="Secure your account!"
      title={<React.Fragment>Create a strong<br />new password.</React.Fragment>}
    >
      <div className="authBox">
        <h2 style={{ position: "relative" }}>
          <img
            src="/static/images/back_arrow.svg"
            alt="back"
            onClick={() => navigate(-1)}
            className="back_arrow"
            style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", cursor: "pointer", width: 24, height: 24 }}
          />
          Reset Password
        </h2>
        <p className="subtitle" style={{ marginBottom: 40 }}>
          Set Your New Password
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group text_field">
            <label htmlFor="new-password">New Password</label>
            <TextField
              id="new-password"
              name="new-password"
              placeholder="Enter New Password"
              fullWidth
              hiddenLabel
              value={newPassword}
              onChange={(e) => handlePasswordChange("new", e.target.value)}
              type={passwordVisibility.new ? "text" : "password"}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.new ? "Hide password" : "Show password"
                      }
                      onClick={() => toggleVisibility("new")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.new ? <VisibilityOff style={{ color: "rgba(255, 255, 255, 0.4)" }} /> : <Visibility style={{ color: "rgba(255, 255, 255, 0.4)" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group text_field">
            <label htmlFor="confirm-password">Confirm Password</label>
            <TextField
              id="confirm-password"
              name="confirm-password"
              placeholder="Enter Confirm Password"
              fullWidth
              hiddenLabel
              value={confirmPassword}
              onChange={(e) => handlePasswordChange("confirm", e.target.value)}
              type={passwordVisibility.confirm ? "text" : "password"}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined style={{ color: "rgba(255, 255, 255, 0.4)" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.confirm ? "Hide password" : "Show password"
                      }
                      onClick={() => toggleVisibility("confirm")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.confirm ? <VisibilityOff style={{ color: "rgba(255, 255, 255, 0.4)" }} /> : <Visibility style={{ color: "rgba(255, 255, 255, 0.4)" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="form_btn" style={{ marginTop: 20 }}>
            <Button
              className="btn btn_primary w_100"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
